import { CHAT_LAUNCH_MAX_ATTEMPTS, CHAT_LAUNCH_RETRY_MS, CHAT_ORIGINS } from "./constants"

/**
 * Warm the chat origins on first intent so DNS+TLS is off the click path. Done from
 * JS, not <head>, where extra connections would compete with the page's own preloads.
 * Listens once on `root`; safe to call again on a fresh root.
 */
export const warmChatOrigins = (root: HTMLElement): void => {
  const warm = (): void => {
    // Whichever intent comes first warms; the other two listeners go with it.
    root.removeEventListener("pointerenter", warm)
    root.removeEventListener("touchstart", warm)
    root.removeEventListener("focusin", warm)
    CHAT_ORIGINS.forEach(href => {
      if (document.head.querySelector(`link[rel="preconnect"][href="${href}"]`)) {
        return
      }
      const link = document.createElement("link")
      link.rel = "preconnect"
      link.href = href
      link.crossOrigin = ""
      document.head.appendChild(link)
    })
  }

  root.addEventListener("pointerenter", warm)
  root.addEventListener("touchstart", warm, { passive: true })
  root.addEventListener("focusin", warm)
}

/** What {@link settleWithin} resolves to when the promise did not settle in time. */
export const SETTLE_TIMED_OUT: unique symbol = Symbol("settle-timed-out")

const wait = (ms: number): Promise<void> =>
  new Promise(resolve => {
    setTimeout(resolve, ms)
  })

/** `promise`'s value, or {@link SETTLE_TIMED_OUT} if it has not settled within `ms`. */
export const settleWithin = <T>(promise: Promise<T>, ms: number): Promise<T | typeof SETTLE_TIMED_OUT> =>
  Promise.race([promise, wait(ms).then((): typeof SETTLE_TIMED_OUT => SETTLE_TIMED_OUT)])

export interface ChatLauncher {
  /** Whether the chat window is up right now. */
  isOpen: () => boolean
  /** One launch attempt. Resolves `false` when the chat cannot open at all. */
  launch: () => Promise<boolean>
}

/**
 * Launch the chat and keep trying until its window reports open.
 *
 * Salesforce silently drops a launch made while its messaging components are still
 * mounting — the promise never settles, and only a later, standalone launch works. So
 * each attempt runs on a fresh macrotask (a launch chained straight off init is the
 * dropped kind) and is given {@link CHAT_LAUNCH_RETRY_MS} before the next one; attempts
 * never overlap. Resolves whether the window opened; rejects only if `launch` does.
 */
export const launchChatUntilOpen = async ({ isOpen, launch }: ChatLauncher): Promise<boolean> => {
  for (let attempt = 0; attempt < CHAT_LAUNCH_MAX_ATTEMPTS; attempt += 1) {
    if (isOpen()) {
      return true
    }
    await wait(0)

    const opened = await settleWithin(launch(), CHAT_LAUNCH_RETRY_MS)
    if (opened !== SETTLE_TIMED_OUT) {
      return opened
    }
  }

  return isOpen()
}
